import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import PublishIcon from "@mui/icons-material/Publish";
import { styled } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import { useConfirmDialog, ConfirmResponse } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import { individualsApi } from "marketplace/individuals/_api/individual-api";
import { enqueueSnackbar } from "notistack";
import React from "react";

const StyledListItemResume = styled(ListItem)(() => ({
  padding: 0,
}));
const StyledLinkResume = styled(Link)(() => ({
  cursor: "pointer",
}));
const StyledButtonResume = styled(LoadingButton)(({ theme }) => ({
  marginLeft: theme.spacing(-1),
}));
const HiddenInput = styled("input")({
  display: "none",
});

export interface ResumeUploadProps {
  accept: string;
  individualId?: Guid;
  userName?: string;
  resume?: Guid | File | null;
  onUpload: (updatedResume: File) => void;
  onDelete: () => void;
}

export default function ResumeUpload(props: Readonly<ResumeUploadProps>) {
  const hiddenResumeFileInput = React.useRef<HTMLInputElement>(null);

  const dispatch = useAttorneyHubDispatch();

  const [uploadingResume, setUploadingResume] = React.useState(false);
  const [deletingResume, setDeletingResume] = React.useState(false);

  const confirm = useConfirmDialog();

  async function handleResumeClick() {
    if (props.resume instanceof File) {
      const blob = new Blob([props.resume], { type: props.resume.type });
      const link = document.createElement("a");
      link.setAttribute("href", globalThis.URL.createObjectURL(blob));
      link.setAttribute("download", props.resume.name);
      link.setAttribute("target", "_blank");
      link.click();
      return;
    }

    try {
      if (!props.individualId) throw new Error("Individual ID not provided");
      const { blob, filename } = await dispatch(
        individualsApi.endpoints.downloadIndividualResume.initiate({ individualId: props.individualId.value }),
      ).unwrap();
      const link = document.createElement("a");
      link.setAttribute("href", globalThis.URL.createObjectURL(blob));
      if (filename) link.setAttribute("download", filename);
      link.setAttribute("target", "_blank");
      link.click();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't download resume. Please try again", { variant: "error" });
    }
  }

  async function handleResumeChange(event: React.ChangeEvent<HTMLInputElement>) {
    setUploadingResume(true);

    const files = event.target.files;

    if (!files || files.length === 0) return;

    const resumeFile = files[0];
    props.onUpload(resumeFile);
    setUploadingResume(false);
  }

  async function handleResumeDelete() {
    const response = await confirm({
      title: "Delete Resume?",
      message: "Are you sure you want to delete this resume?",
      okButtonText: "Delete",
    });

    if (response === ConfirmResponse.Cancel) return;

    if (!props.individualId) {
      props.onDelete();
      return;
    }

    try {
      setDeletingResume(true);
      await dispatch(individualsApi.endpoints.deleteIndividualResume.initiate({ individualId: props.individualId.value })).unwrap();
      props.onDelete();
      enqueueSnackbar("Deleted resume", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't delete resume. Please try again", { variant: "error" });
    } finally {
      setDeletingResume(false);
    }
  }

  return (
    <>
      <List>
        <StyledListItemResume
          disableGutters
          secondaryAction={
            <Tooltip title="Delete Resume">
              <span>
                {props.resume !== null && props.resume !== undefined && (
                  <IconButton edge="end" aria-label="delete" onClick={handleResumeDelete} size="large">
                    {deletingResume ? (
                      <CircularProgress size="24px" sx={{ color: "#999" }} disableShrink />
                    ) : (
                      <DeleteOutlineIcon color="error" />
                    )}
                  </IconButton>
                )}
              </span>
            </Tooltip>
          }
        >
          {props.resume ? (
            <>
              <ListItemIcon>
                <FileOpenIcon color="primary" />
              </ListItemIcon>
              <ListItemText>
                <StyledLinkResume onClick={handleResumeClick}>
                  {props.userName ? `${props.userName}'s Resume` : "View Resume"}
                </StyledLinkResume>
              </ListItemText>
            </>
          ) : (
            <ListItemText primary="No resume uploaded" />
          )}
        </StyledListItemResume>
      </List>
      <HiddenInput type="file" ref={hiddenResumeFileInput} accept={props.accept} onChange={handleResumeChange} />
      <StyledButtonResume
        startIcon={<PublishIcon />}
        loading={uploadingResume}
        onClick={() => hiddenResumeFileInput.current?.click()}
        disabled={Boolean(props.resume) || uploadingResume}
        color="primary"
      >
        Upload Resume
      </StyledButtonResume>
    </>
  );
}
